import { useEffect, useMemo, useRef, useState } from 'react';

// Sample creator faces, saved locally (DiceBear "lorelei", CC BY 4.0 — see /LICENSES)
// so the decorative grid renders instantly instead of round-tripping to the API.
const gridAvatars = import.meta.glob<{ default: string }>(
  '../../assets/avatars/grid/*.svg',
  { eager: true, query: '?url' },
);
const avatarSrc = (seed: string) =>
  gridAvatars[`../../assets/avatars/grid/${seed}.svg`]?.default ?? '';

const CELLS = [
  {
    color: '#8c4238',
    seed: 'cosmic',
    name: 'Zara Nova',
    tag: 'Photographer · LA',
  },
  {
    color: '#524898',
    seed: 'forest',
    name: 'Lena Kai',
    tag: 'Illustrator · Lagos',
  },
  {
    color: '#1e7870',
    seed: 'neon',
    name: 'Marcus Sol',
    tag: 'Producer · Abuja',
  },
  {
    color: '#886020',
    seed: 'golden',
    name: 'Aria Bloom',
    tag: 'Painter · London',
  },
  {
    color: '#483898',
    seed: 'midnight',
    name: 'Devon Ray',
    tag: 'Comedian · Atlanta',
  },
  {
    color: '#885048',
    seed: 'solar',
    name: 'Cleo Wave',
    tag: 'Filmmaker · NYC',
  },
  {
    color: '#207870',
    seed: 'prism',
    name: 'Juno Park',
    tag: 'Musician · LA',
  },
  {
    color: '#885818',
    seed: 'echo',
    name: 'River Stone',
    tag: 'Designer · Berlin',
  },
  {
    color: '#8c4238',
    seed: 'nova',
    name: 'Sasha Reed',
    tag: 'Vocalist · Detroit',
  },
  {
    color: '#524898',
    seed: 'drift',
    name: 'Milo Cruz',
    tag: 'Tattoo Artist · Houston',
  },
  { color: '#1e7870', seed: 'flux', name: 'Yara Moon', tag: 'Poet · London' },
  { color: '#886020', seed: 'wave', name: 'Theo Ash', tag: 'DJ · Toronto' },
];

function FlipCell({
  color,
  seed,
  name,
  tag,
  active,
}: {
  color: string;
  seed: string;
  name: string;
  tag: string;
  active: boolean;
}) {
  return (
    <div className="art-cell">
      <div className="art-cell__front" style={{ background: color }} />
      <div
        className={`art-cell__back${active ? ' art-cell__back--open' : ''}`}
        style={{ background: color }}
      >
        <img
          src={avatarSrc(seed)}
          alt={name}
          className="art-cell__avatar"
          draggable={false}
        />
        <span className="art-cell__name">{name}</span>
        <span className="art-cell__tag">{tag}</span>
      </div>
    </div>
  );
}

export function ArtGrid() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastIdxRef = useRef<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const visibleCells = useMemo(
    () => (isMobile ? CELLS.slice(0, 9) : CELLS),
    [isMobile],
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    function cycle() {
      let next: number;
      do {
        next = Math.floor(Math.random() * visibleCells.length);
      } while (next === lastIdxRef.current);
      lastIdxRef.current = next;

      setActiveIdx(next);

      timerRef.current = setTimeout(
        () => {
          setActiveIdx(null);
          timerRef.current = setTimeout(cycle, 700);
        },
        3500 + Math.random() * 1500,
      );
    }

    timerRef.current = setTimeout(cycle, 800);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visibleCells]);

  return (
    <div className="landing-art-grid" aria-hidden="true">
      {visibleCells.map(({ color, seed, name, tag }, i) => (
        <FlipCell
          key={i}
          color={color}
          seed={seed}
          name={name}
          tag={tag}
          active={activeIdx === i}
        />
      ))}
    </div>
  );
}
